import type { PlayerRole } from "./types.js";

export interface SpawnSpec {
  role: PlayerRole;
  /** Fraccion del ancho medida desde la porteria propia (0 = linea de gol). */
  depth: number;
  /** Fraccion del alto, 0 arriba, 1 abajo. */
  lane: number;
  /** Etiqueta tactica opcional (para futuro editor de formaciones). */
  label?: string;
}

/**
 * Formaciones. **El numero del modo incluye al portero**, como en el futbol de
 * verdad: futbol sala son 5 (4 de campo + portero), futbol 7 son 7 (6 + portero)
 * y futbol 11 son 11 (10 + portero). Asi que un `NvN` tiene N fichas por equipo
 * y N-1 plazas jugables — el portero SIEMPRE lo lleva la IA y no ocupa plaza.
 *
 * `depth` es la fraccion de campo desde la porteria propia y `lane` la fraccion
 * de ancho. `depth: 0.5` esta prohibido: los dos equipos se reflejan sobre el
 * centro y naceran en el mismo punto exacto.
 */

/** 2v2: duelo con porteros. Un hombre de campo por lado. */
export const FORMATION_2V2: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.06, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.4, lane: 0.5, label: "ST" },
] as const;

/** 3v3: portero, un ultimo hombre y una punta. */
export const FORMATION_3V3: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.06, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.22, lane: 0.5, label: "CB" },
  { role: "field", depth: 0.46, lane: 0.5, label: "ST" },
] as const;

/** 4v4: portero y 1-2. */
export const FORMATION_4V4: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.06, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.18, lane: 0.5, label: "CB" },
  { role: "field", depth: 0.44, lane: 0.3, label: "ST" },
  { role: "field", depth: 0.44, lane: 0.7, label: "ST" },
] as const;

/** 5v5: futbol sala exacto — portero y diamante 1-2-1. */
export const FORMATION_5V5: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.06, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.16, lane: 0.5, label: "CB" },
  { role: "field", depth: 0.34, lane: 0.25, label: "CM" },
  { role: "field", depth: 0.34, lane: 0.75, label: "CM" },
  { role: "field", depth: 0.54, lane: 0.5, label: "ST" },
] as const;

/** 6v6: portero y 2-2-1. */
export const FORMATION_6V6: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.06, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.16, lane: 0.34, label: "CB" },
  { role: "field", depth: 0.16, lane: 0.66, label: "CB" },
  { role: "field", depth: 0.36, lane: 0.28, label: "CM" },
  { role: "field", depth: 0.36, lane: 0.72, label: "CM" },
  { role: "field", depth: 0.56, lane: 0.5, label: "ST" },
] as const;

/** 7v7: futbol 7 exacto — portero y 2-3-1. */
export const FORMATION_7V7: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.06, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.14, lane: 0.34, label: "CB" },
  { role: "field", depth: 0.14, lane: 0.66, label: "CB" },
  { role: "field", depth: 0.34, lane: 0.2, label: "LM" },
  { role: "field", depth: 0.34, lane: 0.5, label: "CM" },
  { role: "field", depth: 0.34, lane: 0.8, label: "RM" },
  { role: "field", depth: 0.56, lane: 0.5, label: "ST" },
] as const;

/** 8v8: portero y 3-3-1. */
export const FORMATION_8V8: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.05, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.16, lane: 0.2, label: "LB" },
  { role: "field", depth: 0.14, lane: 0.5, label: "CB" },
  { role: "field", depth: 0.16, lane: 0.8, label: "RB" },
  { role: "field", depth: 0.36, lane: 0.22, label: "LM" },
  { role: "field", depth: 0.34, lane: 0.5, label: "CM" },
  { role: "field", depth: 0.36, lane: 0.78, label: "RM" },
  { role: "field", depth: 0.58, lane: 0.5, label: "ST" },
] as const;

/** 9v9: portero y 3-3-2. */
export const FORMATION_9V9: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.05, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.16, lane: 0.2, label: "LB" },
  { role: "field", depth: 0.14, lane: 0.5, label: "CB" },
  { role: "field", depth: 0.16, lane: 0.8, label: "RB" },
  { role: "field", depth: 0.36, lane: 0.22, label: "LM" },
  { role: "field", depth: 0.34, lane: 0.5, label: "CM" },
  { role: "field", depth: 0.36, lane: 0.78, label: "RM" },
  { role: "field", depth: 0.58, lane: 0.36, label: "ST" },
  { role: "field", depth: 0.58, lane: 0.64, label: "ST" },
] as const;

/** 10v10: portero y 4-3-2. */
export const FORMATION_10V10: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.05, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.2, lane: 0.14, label: "LB" },
  { role: "field", depth: 0.16, lane: 0.38, label: "CB" },
  { role: "field", depth: 0.16, lane: 0.62, label: "CB" },
  { role: "field", depth: 0.2, lane: 0.86, label: "RB" },
  { role: "field", depth: 0.38, lane: 0.24, label: "LM" },
  { role: "field", depth: 0.36, lane: 0.5, label: "CM" },
  { role: "field", depth: 0.38, lane: 0.76, label: "RM" },
  { role: "field", depth: 0.58, lane: 0.36, label: "ST" },
  { role: "field", depth: 0.58, lane: 0.64, label: "ST" },
] as const;

/** @deprecated Reservado: 2v2 con portero dedicado. */
export const FORMATION_2V2_GK: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.08, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.38, lane: 0.5, label: "ST" },
] as const;

/** @deprecated Reservado: 3v3 con portero dedicado. */
export const FORMATION_3V3_GK: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.06, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.32, lane: 0.3, label: "ST" },
  { role: "field", depth: 0.32, lane: 0.7, label: "ST" },
] as const;

/** @deprecated Reservado: 5v5 con portero dedicado. */
export const FORMATION_5V5_GK: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.07, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.22, lane: 0.5, label: "CB" },
  { role: "field", depth: 0.4, lane: 0.22, label: "CM" },
  { role: "field", depth: 0.4, lane: 0.78, label: "CM" },
  { role: "field", depth: 0.62, lane: 0.5, label: "ST" },
] as const;

/** 4-4-2 clasico: portero + cuatro defensas + cuatro medios + dos delanteros. */
export const FORMATION_442: readonly SpawnSpec[] = [
  { role: "gk", depth: 0.05, lane: 0.5, label: "GK" },
  { role: "field", depth: 0.2, lane: 0.12, label: "LB" },
  { role: "field", depth: 0.17, lane: 0.35, label: "CB" },
  { role: "field", depth: 0.17, lane: 0.65, label: "CB" },
  { role: "field", depth: 0.2, lane: 0.88, label: "RB" },
  { role: "field", depth: 0.38, lane: 0.12, label: "LM" },
  { role: "field", depth: 0.36, lane: 0.38, label: "CM" },
  { role: "field", depth: 0.36, lane: 0.62, label: "CM" },
  { role: "field", depth: 0.38, lane: 0.88, label: "RM" },
  { role: "field", depth: 0.58, lane: 0.35, label: "ST" },
  { role: "field", depth: 0.58, lane: 0.65, label: "ST" },
] as const;

/**
 * Asientos de campo ordenados por cuanto se participa desde ellos: primero el
 * mas adelantado, y a igual profundidad el mas centrado.
 *
 * Existe porque el reparto de asientos NO puede ser un numero a mano. Medido
 * con `pnpm ai:audit`: el humano en un medio tocaba el balon **0,48x** lo que
 * lo tocaba un companero, y no tiraba ni una vez por partido. El asiento decide
 * la primera impresion de un jugador nuevo, asi que se calcula de la formacion
 * y se mantiene solo cuando esta cambia.
 */
export function seatPriority(formation: readonly SpawnSpec[]): number[] {
  return formation
    .map((spec, index) => ({ spec, index }))
    .filter(({ spec }) => spec.role !== "gk")
    .sort((a, b) => {
      const depth = b.spec.depth - a.spec.depth;
      if (Math.abs(depth) > 0.001) return depth;
      return Math.abs(a.spec.lane - 0.5) - Math.abs(b.spec.lane - 0.5);
    })
    .map(({ index }) => index);
}

/** Asiento mas ofensivo de la formacion: el que se le da al humano. */
export function attackingSeatIndex(formation: readonly SpawnSpec[]): number {
  return seatPriority(formation)[0] ?? 1;
}
